import $ from "jquery";
import Dropzone from "dropzone";
import { InCoreToast, InCoreToastType } from "./toast.utils";
import { CloseModal, InCoreModal, InCoreModalAction, InCoreModalLayout } from "./modal.utils";
import { InCoreDTActions, InCoreDTFromModel } from "./datatable.utils";
import { initDropzone } from "./dropzone.utils";
import { InCoreSelect2 } from "./select2.utils";

const FOLDER_PARAM = "folderId";

interface FoldersBrowseResult {
    folderId: number | null;
    breadcrumb: { id: number; name: string }[];
    folders: { id: number; name: string }[];
}

interface FoldersInstance {
    root: JQuery<HTMLElement>;
    controller: string;
    idRiferimento: string;
    rootLabel: string;
    currentFolderId: number | null;
    onChange: (folderId: number | null) => void;
}

const instances = new Map<string, FoldersInstance>();
let popstateBound = false;

/**
 * Legge l'id cartella dalla query string della pagina (link diretti alla cartella).
 * @param {string} param - Nome del parametro (default: folderId).
 * @returns {number | null} - L'id cartella, o null se assente/non valido.
 */
export function InCoreFoldersFromUrl(param: string = FOLDER_PARAM): number | null {
    const value = parseInt(new URLSearchParams(window.location.search).get(param) ?? "", 10);
    return isNaN(value) ? null : value;
}

/**
 * Restituisce la cartella corrente del componente ('' se radice: comodo da passare come parametro a InCoreModal).
 * @param {string} id - Id del componente.
 * @returns {number | ''} - Id cartella corrente.
 */
export function InCoreFoldersGetCurrent(id: string): number | "" {
    return instances.get(id)?.currentFolderId ?? "";
}

/**
 * Inizializza il componente cartelle (partial _InCoreFolders).
 * @param {string} id - Id del componente.
 * @param {number | null} initialFolderId - Cartella iniziale (di solito InCoreFoldersFromUrl()).
 * @param {Function} onChange - Chiamata quando la cartella corrente cambia dopo l'init (ricaricare la DataTable).
 *                              Viene chiamata anche se la cartella iniziale non era valida e si è tornati alla radice.
 */
export function InCoreFoldersInit(id: string, initialFolderId: number | null, onChange: (folderId: number | null) => void): void {
    const root = $(`#${id}`);
    if (root.length === 0) {
        return;
    }

    const instance: FoldersInstance = {
        root: root,
        controller: root.attr("data-controller") ?? "",
        idRiferimento: root.attr("data-idriferimento") ?? "",
        rootLabel: root.attr("data-rootlabel") ?? "Documenti",
        currentFolderId: initialFolderId,
        onChange: onChange,
    };
    instances.set(id, instance);

    bindCreate(id, instance);

    if (!popstateBound) {
        popstateBound = true;
        window.addEventListener("popstate", () => {
            instances.forEach((inst, key) => {
                if (!document.body.contains(inst.root[0])) {
                    instances.delete(key);
                    return;
                }
                const folderId = InCoreFoldersFromUrl();
                if (folderId !== inst.currentFolderId) {
                    navigate(key, inst, folderId, "none", true);
                }
            });
        });
    }

    navigate(id, instance, initialFolderId, "replace", false);
}

/**
 * Ricarica breadcrumb e cartelle figlie della cartella corrente (es. dopo la creazione di una cartella).
 * @param {string} id - Id del componente.
 */
export function InCoreFoldersRefresh(id: string): void {
    const instance = instances.get(id);
    if (instance) {
        navigate(id, instance, instance.currentFolderId, "none", false);
    }
}

/**
 * Apre una cartella (aggiorna breadcrumb, URL e tabella): per le cartelle mostrate come righe della DataTable.
 * @param {string} id - Id del componente.
 * @param {number | null} folderId - Cartella da aprire (null = radice).
 */
export function InCoreFoldersNavigate(id: string, folderId: number | null): void {
    const instance = instances.get(id);
    if (instance) {
        navigate(id, instance, folderId, "push", true);
    }
}

const DRAG_CLASS = "incore-folders-dragging";
const DROP_CLASS = "incore-folders-droptarget";

let dragging: { id: number; tipo: "Cartella" | "File" } | null = null;

/**
 * Abilita lo spostamento delle righe della DataTable trascinandole su una cartella (riga) o sul breadcrumb.
 * Solo su dispositivi con puntatore preciso e hover (desktop): su touch resta "Sposta in…".
 * Le righe devono avere i campi ID e TipoRiga ("Cartella" / "File").
 * @param {string} id - Id del componente cartelle.
 * @param {DataTables.Api} dt - La DataTable delle righe.
 * @param {boolean} enabled - Se l'utente può spostare (stessi permessi di "Sposta in…").
 * @param {Function} onMoved - Chiamata dopo uno spostamento riuscito (ricaricare la DataTable).
 */
export function InCoreFoldersEnableDragDrop(id: string, dt: DataTables.Api, enabled: boolean, onMoved: () => void): void {
    const instance = instances.get(id);
    if (!instance || !enabled || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        return;
    }

    const body = $(dt.table(undefined).body());
    const rowOf = (el: HTMLElement) => dt.row($(el).closest("tr")).data() as { ID?: number; TipoRiga?: string } | undefined;
    const clearTargets = () => $(`.${DROP_CLASS}`).removeClass(DROP_CLASS);

    // Un drag non nostro (file dal desktop verso la dropzone) non deve essere toccato: tutto dipende da `dragging`
    const canDropOn = (destinationId: number | null) => dragging !== null && !(dragging.tipo === "Cartella" && dragging.id === destinationId);

    const drop = (destinationId: number | null) => {
        const item = dragging;
        dragging = null;
        clearTargets();
        if (!item) {
            return; // i chiamanti hanno già verificato canDropOn
        }

        $.ajax({
            type: "POST",
            url: `/${instance.controller}/Sposta/${item.id}`,
            data: {
                tipo: item.tipo,
                destinazioneId: destinationId ?? "",
                __RequestVerificationToken: instance.root.find("[name='__RequestVerificationToken']").first().val(),
            },
            success: () => {
                InCoreToast(InCoreToastType.Success, "Documento spostato con successo.");
                onMoved();
            },
            error: (xhr) => {
                InCoreToast(xhr.status === 409 ? InCoreToastType.Warning : InCoreToastType.Error, xhr.responseText || "Impossibile spostare l'elemento.");
            },
        });
    };

    // Le righe vengono ridisegnate a ogni caricamento: l'attributo va rimesso a ogni draw
    const markRows = () => body.find("tr").attr("draggable", "true");
    dt.off("draw.incoreFoldersDrag").on("draw.incoreFoldersDrag", markRows);
    markRows();

    body.off(".incoreFoldersDrag")
        .on("dragstart.incoreFoldersDrag", "tr", (e) => {
            const data = rowOf(e.currentTarget);
            if (!data || data.ID === undefined || (data.TipoRiga !== "Cartella" && data.TipoRiga !== "File")) {
                return;
            }
            dragging = { id: data.ID, tipo: data.TipoRiga };
            const original = e.originalEvent as DragEvent;
            original.dataTransfer?.setData("text/plain", String(data.ID)); // Firefox non avvia il drag senza dati
            if (original.dataTransfer) {
                original.dataTransfer.effectAllowed = "move";
            }
            $(e.currentTarget).addClass(DRAG_CLASS);
        })
        .on("dragend.incoreFoldersDrag", "tr", () => {
            dragging = null;
            clearTargets();
            body.find(`.${DRAG_CLASS}`).removeClass(DRAG_CLASS);
        })
        .on("dragover.incoreFoldersDrag", "tr", (e) => {
            const data = rowOf(e.currentTarget);
            if (data?.TipoRiga === "Cartella" && data.ID !== undefined && canDropOn(data.ID)) {
                e.preventDefault();
                clearTargets();
                $(e.currentTarget).addClass(DROP_CLASS);
            }
        })
        .on("dragleave.incoreFoldersDrag", "tr", (e) => {
            // Uscendo verso un elemento figlio della stessa riga il bersaglio resta evidenziato
            if (!e.currentTarget.contains((e.originalEvent as DragEvent).relatedTarget as Node | null)) {
                $(e.currentTarget).removeClass(DROP_CLASS);
            }
        })
        .on("drop.incoreFoldersDrag", "tr", (e) => {
            const data = rowOf(e.currentTarget);
            if (data?.TipoRiga === "Cartella" && data.ID !== undefined && canDropOn(data.ID)) {
                e.preventDefault();
                drop(data.ID);
            }
        });

    // Breadcrumb: i link (radice compresa) sono destinazioni valide; la cartella corrente è uno span, quindi esclusa
    const parse = (el: HTMLElement) => {
        const raw = $(el).attr("data-folder-id");
        return raw === undefined || raw === "" ? null : parseInt(raw, 10);
    };
    instance.root.off(".incoreFoldersDrag")
        .on("dragover.incoreFoldersDrag", ".incore-folders-crumb", (e) => {
            if (canDropOn(parse(e.currentTarget))) {
                e.preventDefault();
                clearTargets();
                $(e.currentTarget).addClass(DROP_CLASS);
            }
        })
        .on("dragleave.incoreFoldersDrag", ".incore-folders-crumb", (e) => {
            $(e.currentTarget).removeClass(DROP_CLASS);
        })
        .on("drop.incoreFoldersDrag", ".incore-folders-crumb", (e) => {
            if (canDropOn(parse(e.currentTarget))) {
                e.preventDefault();
                drop(parse(e.currentTarget));
            }
        });
}

function buildUrl(folderId: number | null): string {
    const params = new URLSearchParams(window.location.search);
    if (folderId === null) {
        params.delete(FOLDER_PARAM);
    } else {
        params.set(FOLDER_PARAM, folderId.toString());
    }
    const query = params.toString();
    return window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
}

function navigate(id: string, instance: FoldersInstance, folderId: number | null, history: "push" | "replace" | "none", notify: boolean): void {
    $.ajax({
        type: "GET",
        url: `/${instance.controller}/Browse/${instance.idRiferimento}`,
        data: folderId === null ? {} : { folderId: folderId },
        dataType: "json",
        success: (result: FoldersBrowseResult) => {
            const resolved = result.folderId ?? null;
            const changed = resolved !== instance.currentFolderId;
            instance.currentFolderId = resolved;
            render(id, instance, result);

            if (history === "push" && changed) {
                window.history.pushState({ incoreFolders: id }, "", buildUrl(resolved));
            } else if (history === "replace" || history === "push") {
                window.history.replaceState(window.history.state, "", buildUrl(resolved));
            }

            // Se il server ha riportato alla radice una cartella non valida, la tabella va riallineata
            if (notify || resolved !== folderId) {
                instance.onChange(resolved);
            }
        },
        error: (xhr) => {
            InCoreToast(InCoreToastType.Error, xhr.responseText || "Impossibile caricare le cartelle.");
        },
    });
}

function folderLink(label: string, folderId: number | null, cssClass: string, icon?: string): JQuery<HTMLElement> {
    const link = $("<a>").attr("href", buildUrl(folderId)).addClass(cssClass).attr("data-folder-id", folderId ?? "");
    if (icon) {
        link.append($("<i>").addClass("incore-mi-normal").text(icon));
    }
    link.append($("<span>").text(label));
    return link;
}

function render(id: string, instance: FoldersInstance, result: FoldersBrowseResult): void {
    const handleClick = (e: JQuery.ClickEvent) => {
        // Ctrl/Cmd/Shift/click centrale: lascio il comportamento nativo del link (nuova scheda)
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.which === 2) {
            return;
        }
        e.preventDefault();
        const raw = $(e.currentTarget).attr("data-folder-id");
        const target = raw === undefined || raw === "" ? null : parseInt(raw, 10);
        navigate(id, instance, target, "push", true);
    };

    const nav = instance.root.find(".incore-folders-breadcrumb").empty();
    const list = $("<ol>").addClass("incore-folders-crumbs");
    const last = result.breadcrumb.length - 1;

    const rootItem = $("<li>");
    if (last < 0) {
        rootItem.append($("<span>").addClass("incore-folders-crumb-current").append($("<i>").addClass("incore-mi-normal").text("home"), $("<span>").text(instance.rootLabel)));
    } else {
        rootItem.append(folderLink(instance.rootLabel, null, "incore-folders-crumb", "home").on("click", handleClick));
    }
    list.append(rootItem);

    result.breadcrumb.forEach((item, index) => {
        const li = $("<li>");
        if (index === last) {
            li.append($("<span>").addClass("incore-folders-crumb-current").text(item.name));
        } else {
            li.append(folderLink(item.name, item.id, "incore-folders-crumb").on("click", handleClick));
        }
        list.append(li);
    });
    nav.append(list);

    const container = instance.root.find(".incore-folders-list").empty();
    if (instance.root.attr("data-showcards") !== "false") {
        result.folders.forEach((folder) => {
            container.append(folderLink(folder.name, folder.id, "incore-folders-card", "folder").on("click", handleClick));
        });
    }

    // Porto in vista l'ultimo elemento del breadcrumb sugli schermi stretti
    const el = nav[0];
    if (el) {
        el.scrollLeft = el.scrollWidth;
    }
}

/**
 * Apre la modale "Nuova cartella" (o "Nuova sottocartella") nella cartella corrente: per chi non usa il pulsante "Nuova cartella"
 * del componente ma una propria voce di menu (es. "Crea o carica").
 * @param {string} id - Id del componente.
 */
export function InCoreFoldersOpenCreate(id: string): void {
    const instance = instances.get(id);
    if (!instance) {
        return;
    }

    InCoreModal(
        instance.controller,
        InCoreModalAction.CREATE,
        new InCoreModalLayout().ButtonsBelowX(true, true),
        instance.idRiferimento,
        () => {
            InCoreToast(InCoreToastType.Success, "Cartella creata");
            // notify = true: la cartella appena creata è una riga della DataTable, che va ricaricata
            navigate(id, instance, instance.currentFolderId, "none", true);
        },
        "NuovaCartella",
        "parentId",
        instance.currentFolderId ?? ""
    );
}

function bindCreate(id: string, instance: FoldersInstance): void {
    const root = instance.root;
    const box = root.find(".incore-folders-create");
    const input = root.find(".incore-folders-create-name");

    const close = () => {
        box.hide();
        input.val("");
    };

    const submit = () => {
        const name = ((input.val() as string) ?? "").trim();
        if (!name) {
            InCoreToast(InCoreToastType.Warning, "Inserire il nome della cartella.");
            return;
        }

        $.ajax({
            type: "POST",
            url: `/${instance.controller}/CreateFolder/${instance.idRiferimento}`,
            data: {
                parentId: instance.currentFolderId ?? "",
                name: name,
                __RequestVerificationToken: root.find("[name='__RequestVerificationToken']").first().val(),
            },
            success: () => {
                close();
                InCoreToast(InCoreToastType.Success, "Cartella creata");
                // notify = true: la cartella appena creata è una riga della DataTable, che va ricaricata
                navigate(id, instance, instance.currentFolderId, "none", true);
            },
            error: (xhr) => {
                InCoreToast(xhr.status === 409 || xhr.status === 400 ? InCoreToastType.Warning : InCoreToastType.Error, xhr.responseText || "Impossibile creare la cartella.");
            },
        });
    };

    root.find(".incore-folders-new").off("click").on("click", () => {
        box.show();
        input.trigger("focus");
    });
    root.find(".incore-folders-create-ok").off("click").on("click", submit);
    root.find(".incore-folders-create-cancel").off("click").on("click", close);
    input.off("keydown").on("keydown", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            submit();
        } else if (e.key === "Escape") {
            close();
        }
    });
}



/**
 * Scelta da fare per ogni file (es. tipo documento), definita dal progetto (InCoreFolderUploadSelect).
 */
export interface InCoreFileUploadSelect {
    Name: string;
    Label: string;
    AjaxUrl: string;
    Required: boolean;
    DefaultValue?: string | null;
    DefaultText?: string | null;
}

/**
 * Opzioni della modale "Carica file" (partial _InCoreFileUpload).
 */
export interface InCoreFileUploadOptions {
    /** Messaggio mostrato a caricamento concluso senza errori. */
    successMessage: string;
    /** Scelta per file richiesta dal progetto (null = nessuna). */
    select: InCoreFileUploadSelect | null;
}

/**
 * Opzioni della modale "Carica cartella" (partial _InCoreFolderUpload).
 */
export interface InCoreFolderUploadOptions {
    /** Messaggio mostrato a caricamento concluso senza errori. */
    successMessage: string;
}

type UploadFile = Dropzone.DropzoneFile & { incoreRow?: JQuery };

interface FolderNode {
    name: string;
    depth: number;
    /** File direttamente nella cartella. */
    files: number;
    /** File nella cartella e in tutte le sottocartelle. */
    totalFiles: number;
    /** Sottocartelle a qualsiasi livello. */
    subfolders: number;
}

interface UploadQueueConfig {
    successMessage: string;
    /** Avviso se si preme Carica senza aver scelto nulla. */
    emptyMessage: string;
    /** Controlli prima dell'avvio: false blocca il caricamento (il controllo avvisa da sé l'utente). */
    validate?: (queued: UploadFile[]) => boolean;
    onAddedFile: (file: UploadFile) => void;
    onRemovedFile: (file: UploadFile) => void;
}

/** File di sistema che non vanno caricati. */
const IGNORED_FILES = ["thumbs.db", "desktop.ini", ".ds_store"];
const IMAGE = [".png", ".jpg", ".jpeg", ".gif", ".svg", ".bmp", ".webp"];
const VIDEO = [".mp4", ".mov", ".avi", ".webm", ".mkv"];
const WORD = [".doc", ".docx", ".odt", ".rtf"];
const EXCEL = [".xls", ".xlsx", ".ods", ".csv"];

let rowCounter = 0;

function plural(count: number, one: string, many: string): string {
    return `${count} ${count === 1 ? one : many}`;
}

function isIgnored(name: string): boolean {
    const lower = (name || "").toLowerCase();
    return IGNORED_FILES.includes(lower) || lower.startsWith("~$");
}

/** Icona e colore del file, come InCoreFileIcon lato server (classi incore-file-icon-* in folders.css). */
function fileIcon(name: string): { icon: string; kind: string } {
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 ? name.substring(dot).toLowerCase() : "";

    if (ext === ".pdf") return { icon: "description", kind: "pdf" };
    if (IMAGE.includes(ext)) return { icon: "description", kind: "image" };
    if (VIDEO.includes(ext)) return { icon: "videocam", kind: "video" };
    if (WORD.includes(ext)) return { icon: "description", kind: "word" };
    if (EXCEL.includes(ext)) return { icon: "description", kind: "excel" };
    return { icon: "description", kind: "file" };
}

/** Percorso relativo del file: fullPath lo imposta Dropzone al trascinamento, webkitRelativePath la selezione con webkitdirectory. */
function relativePath(file: File): string {
    return (file as any).fullPath || file.webkitRelativePath || file.name;
}

/**
 * Parte comune delle modali di caricamento: un file per richiesta (data-paralleluploads=1), raccolta degli errori,
 * chiusura con toast a fine coda e pulsante Salva. Dropzone, senza autoprocess, non avvia da solo il file successivo.
 * @returns isStarted: se il caricamento è partito (Dropzone toglie ogni file dalla lista appena concluso).
 */
function setupUploadQueue(dropzone: Dropzone, config: UploadQueueConfig): { isStarted: () => boolean } {
    let started = false;
    const failed: { name: string; text: string }[] = [];

    dropzone.on("completemultiple", () => {
        if (dropzone.getUploadingFiles().length === 0 && dropzone.getQueuedFiles().length > 0) {
            dropzone.processQueue();
        }
    });

    // I file scartati (troppo grandi, estensione non ammessa...) o falliti spariscono dalla lista al "complete": si raccolgono per segnalarli a fine caricamento
    dropzone.on("error", (file: Dropzone.DropzoneFile, message: any) => {
        const text = typeof message === "string" ? message : message?.detail || message?.title || "Errore";
        failed.push({ name: file.name, text: text });
    });

    dropzone.on("queuecomplete", () => {
        if (!started) {
            return;
        }
        started = false;

        const errors = failed.splice(0);
        CloseModal(true);

        if (errors.length === 0) {
            InCoreToast(InCoreToastType.Success, config.successMessage);
        } else {
            InCoreToast(InCoreToastType.Warning, `${plural(errors.length, "file non caricato", "file non caricati")}. Primo errore: ${errors[0].name} (${errors[0].text})`);
        }
    });

    dropzone.on("addedfile", (file: UploadFile) => config.onAddedFile(file));
    dropzone.on("removedfile", (file: UploadFile) => config.onRemovedFile(file));

    $(".InCoreModalBtnSave").off("click").on("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (started) {
            return;
        }

        const queued = dropzone.getQueuedFiles() as UploadFile[];
        if (queued.length === 0) {
            InCoreToast(InCoreToastType.Warning, config.emptyMessage);
            return;
        }

        if (config.validate && !config.validate(queued)) {
            return;
        }

        started = true;
        dropzone.processQueue();
    });

    return { isStarted: () => started };
}

// ---------------------------------------------------------------------------------------------------------------------
// Sposta in / Copia in
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Dati della modale "Sposta in" / "Copia in" (partial _InCoreFolderMove): tutte le cartelle di destinazione possibili.
 */
export interface InCoreFolderMoveData {
    rootLabel: string;
    /** Elementi nella radice (sottocartelle e documenti), null se non disponibile. */
    rootCount: number | null;
    folders: { id: number; name: string; parentId: number | null; count: number | null }[];
}

/**
 * Inizializza la scelta della destinazione di "Sposta in" / "Copia in": si vedono le cartelle di un livello alla volta, con il numero di elementi.
 * Un clic su una cartella la sceglie come destinazione (un secondo clic toglie la scelta), la freccia a destra la apre.
 * Senza scelta nel livello la destinazione è il livello stesso. La destinazione va al campo nascosto destinazioneId del form.
 * @param {InCoreFolderMoveData} data - Cartelle di destinazione.
 */
export function InCoreFolderMoveInit(data: InCoreFolderMoveData): void {
    const bar = $("#incoreFolderMoveBar");
    const list = $("#incoreFolderMoveList");
    const destination = $("#incoreFolderDestination");
    const value = $("#incoreFolderMoveValue");
    const byId = new Map(data.folders.map((f) => [f.id, f]));

    let levelId: number | null = null;
    // Sottocartella scelta nel livello mostrato; null = nessuna, la destinazione è il livello stesso
    let selectedId: number | null = null;

    /** Cartelle dalla radice fino alla cartella indicata (compresa). */
    const chainOf = (id: number | null): { id: number; name: string }[] => {
        const chain: { id: number; name: string }[] = [];
        const visited = new Set<number>();
        let current = id;
        while (current !== null && byId.has(current) && visited.add(current)) {
            const folder = byId.get(current)!;
            chain.unshift({ id: folder.id, name: folder.name });
            current = folder.parentId;
        }
        return chain;
    };

    const countText = (count: number | null): string => (count === null ? "" : plural(count, "elemento", "elementi"));

    const goTo = (id: number | null): void => {
        levelId = id;
        selectedId = null;
        render();
    };

    const renderBar = (): void => {
        bar.empty();

        if (levelId !== null) {
            const back = $('<button type="button" class="incore-fmove-back" title="Indietro"><i class="incore-mi-normal">chevron_left</i></button>');
            back.on("click", () => goTo(byId.get(levelId!)?.parentId ?? null));
            bar.append(back);
        }

        const crumbs = $('<ul class="incore-folders-crumbs"></ul>');
        const chain = chainOf(levelId);
        const addCrumb = (id: number | null, label: string, current: boolean, icon?: string): void => {
            const content = (icon ? `<i class="incore-mi-normal">${icon}</i>` : "") + $("<span></span>").text(label).prop("outerHTML");
            const item = $("<li></li>");
            if (current) {
                item.append($('<span class="incore-folders-crumb-current"></span>').html(content));
            } else {
                item.append($('<a href="#" class="incore-folders-crumb"></a>').html(content).on("click", (e) => {
                    e.preventDefault();
                    goTo(id);
                }));
            }
            crumbs.append(item);
        };

        addCrumb(null, data.rootLabel, chain.length === 0, "home");
        chain.forEach((folder, index) => addCrumb(folder.id, folder.name, index === chain.length - 1));
        bar.append(crumbs);
    };

    const renderList = (): void => {
        list.empty();
        const children = data.folders.filter((f) => f.parentId === levelId);

        if (children.length === 0) {
            list.append($('<div class="incore-fmove-empty"></div>').text("Nessuna sottocartella"));
            return;
        }

        for (const folder of children) {
            const selected = folder.id === selectedId;
            const row = $('<div class="incore-folders-tree-item incore-fmove-item" role="button" tabindex="0"></div>').toggleClass("is-selected", selected);
            row.append($('<i class="incore-mi-normal incore-file-icon-folder">folder</i>'));
            row.append($("<span></span>").text(folder.name));
            row.append($('<em class="incore-fmove-count"></em>').text(countText(folder.count)));
            if (selected) {
                row.append($('<i class="incore-mi-normal incore-fmove-check">check_circle</i>'));
            }

            const open = $('<button type="button" class="incore-fmove-open" title="Apri"><i class="incore-mi-normal">chevron_right</i></button>');
            open.on("click", (e) => {
                e.stopPropagation();
                goTo(folder.id);
            });
            row.append(open);

            const select = (): void => {
                selectedId = selected ? null : folder.id;
                render();
            };
            row.on("click", select);
            row.on("keydown", (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select();
                }
            });
            list.append(row);
        }
    };

    const render = (): void => {
        renderBar();
        renderList();

        // Destinazione: la sottocartella scelta, altrimenti il livello mostrato
        const destinationId = selectedId ?? levelId;
        value.val(destinationId ?? "");
        destination.text([data.rootLabel, ...chainOf(destinationId).map((f) => f.name)].join(" › "));
    };

    render();
}

// ---------------------------------------------------------------------------------------------------------------------
// Carica file
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Inizializza la modale "Carica file": Dropzone, elenco dei file scelti (con la scelta per file richiesta dal progetto e il cestino)
 * e caricamento un file per volta. Da chiamare dal partial _InCoreFileUpload.
 * @param {InCoreFileUploadOptions} options - Opzioni della modale.
 */
export function InCoreFileUploadInit(options: InCoreFileUploadOptions): void {
    const dropzone = initDropzone("incoreFileUploadDropzone") as Dropzone | undefined;
    if (!dropzone) {
        return;
    }

    const select = options.select;

    const updateCount = (): void => {
        const count = $("#incoreFileUploadList").children().length;
        $("#incoreFileUploadCount").text(count);
        $("#incoreFileUploadSelection").prop("hidden", count === 0);
    };

    const selectOf = (file: UploadFile): JQuery => (file.incoreRow ?? $()).find("select");

    const addRow = (file: UploadFile): void => {
        const { icon, kind } = fileIcon(file.name);
        const row = $('<div class="incore-fupload-file"></div>');
        row.append($('<i class="incore-mi-normal"></i>').addClass(`incore-file-icon-${kind}`).text(icon));
        row.append($('<span class="incore-fupload-file-name"></span>').text(file.name).attr("title", file.name));

        if (select) {
            const id = `incoreFileUploadSelect_${++rowCounter}`;
            const field = $('<div class="incore-fupload-file-type incore-flex w-100"></div>');
            field.append($("<label class='control-label'></label>").attr("for", id).text(select.Label));
            const control = $('<select class="form-control" data-allowclear="true"></select>').attr({ id: id, "data-ajax": select.AjaxUrl });
            if (select.DefaultValue) {
                control.append(new Option(select.DefaultText ?? select.DefaultValue, select.DefaultValue, true, true));
            }
            field.append(control);
            row.append(field);
        }

        const remove = $('<button type="button" class="incore-fupload-file-remove" title="Elimina"><i class="incore-mi-outlined">delete</i></button>');
        remove.on("click", () => dropzone.removeFile(file));
        row.append(remove);

        file.incoreRow = row;
        $("#incoreFileUploadList").append(row);

        if (select) {
            InCoreSelect2(row.find("select").attr("id")!);
        }
        updateCount();
    };

    setupUploadQueue(dropzone, {
        successMessage: options.successMessage,
        emptyMessage: "Seleziona almeno un file da caricare.",
        onAddedFile: addRow,
        onRemovedFile: (file) => {
            file.incoreRow?.remove();
            updateCount();
        },
        validate: (queued) => {
            if (!select?.Required) {
                return true;
            }

            let valid = true;
            queued.forEach((file) => {
                const control = selectOf(file);
                const missing = !control.val();
                control.toggleClass("input-validation-error", missing);
                control.next(".select2-container").find(".select2-selection").toggleClass("input-validation-error", missing);
                valid = valid && !missing;
            });

            if (!valid) {
                InCoreToast(InCoreToastType.Warning, `Seleziona ${select.Label.toLowerCase()} per ogni file.`);
            }
            return valid;
        }
    });

    // La scelta del file si aggiunge al modello JSON già preparato da Dropzone (campi del form), così ogni file ha la sua
    dropzone.on("sendingmultiple", (files: UploadFile[], _xhr: XMLHttpRequest, formData: FormData) => {
        if (!select || files.length === 0) {
            return;
        }

        const model = JSON.parse((formData.get("modelStr") as string) || "{}");
        const value = selectOf(files[0]).val();
        if (value) {
            model[select.Name] = value;
        }
        formData.set("modelStr", JSON.stringify(model));
    });
}

// ---------------------------------------------------------------------------------------------------------------------
// Carica cartella
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Costruisce l'elenco delle cartelle (nell'ordine di visualizzazione) dai percorsi relativi dei file.
 * I file senza cartella (trascinati da soli) sono restituiti a parte.
 */
function buildTree(files: File[]): { folders: FolderNode[]; looseFiles: number } {
    const nodes = new Map<string, FolderNode>();
    let looseFiles = 0;

    for (const file of files) {
        const segments = relativePath(file).split(/[\\/]/).filter((s) => s.length > 0);
        const folderNames = segments.slice(0, -1);

        if (folderNames.length === 0) {
            looseFiles++;
            continue;
        }

        let key = "";
        folderNames.forEach((name, index) => {
            key += "/" + name;
            if (!nodes.has(key)) {
                nodes.set(key, { name: name, depth: index, files: 0, totalFiles: 0, subfolders: 0 });
            }

            const node = nodes.get(key)!;
            node.totalFiles++;
            if (index === folderNames.length - 1) {
                node.files++;
            }
        });
    }

    const keys = Array.from(nodes.keys()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    for (const key of keys) {
        const node = nodes.get(key)!;
        node.subfolders = keys.filter((k) => k.startsWith(key + "/")).length;
    }

    return { folders: keys.map((k) => nodes.get(k)!), looseFiles: looseFiles };
}

function renderTree(files: File[]): void {
    const selection = $("#incoreFolderUploadSelection");
    const tree = $("#incoreFolderUploadTree").empty();
    const { folders, looseFiles } = buildTree(files);

    selection.prop("hidden", folders.length === 0 && looseFiles === 0);

    const addRow = (icon: string, iconClass: string, name: string, info: string, depth: number): void => {
        const row = $('<div class="incore-folders-tree-item incore-fupload-item"></div>');
        row.css("padding-left", `${12 + depth * 20}px`);
        row.append($('<i class="incore-mi-normal"></i>').addClass(iconClass).text(icon));
        row.append($("<span></span>").text(name));
        row.append($('<em class="incore-fupload-count"></em>').text(info));
        tree.append(row);
    };

    for (const folder of folders) {
        // Il livello principale riassume tutto il contenuto, le sottocartelle riportano i propri file
        const info = folder.depth === 0 && folder.subfolders > 0
            ? `${plural(folder.totalFiles, "file", "file")} · ${plural(folder.subfolders, "sottocartella", "sottocartelle")}`
            : plural(folder.files, "file", "file");
        addRow("folder", "incore-file-icon-folder", folder.name, info, folder.depth);
    }

    if (looseFiles > 0) {
        addRow("insert_drive_file", "incore-file-icon-file", "File singoli", plural(looseFiles, "file", "file"), 0);
    }
}

/**
 * Inizializza la modale "Carica cartella": Dropzone con selezione o trascinamento di una cartella intera, anteprima della struttura
 * e caricamento un file per volta. Da chiamare dal partial _InCoreFolderUpload.
 * @param {InCoreFolderUploadOptions} options - Opzioni della modale.
 */
export function InCoreFolderUploadInit(options: InCoreFolderUploadOptions): void {
    const dropzone = initDropzone("incoreFolderUploadDropzone") as Dropzone | undefined;
    if (!dropzone) {
        return;
    }

    // A caricamento avviato Dropzone toglie ogni file dalla lista appena concluso: l'anteprima resta quella scelta
    const queue = setupUploadQueue(dropzone, {
        successMessage: options.successMessage,
        emptyMessage: "Seleziona una cartella da caricare.",
        onAddedFile: (file) => {
            if (isIgnored(file.name)) {
                dropzone.removeFile(file);
                return;
            }
            if (!queue.isStarted()) {
                renderTree(dropzone.files);
            }
        },
        onRemovedFile: () => {
            if (!queue.isStarted()) {
                renderTree(dropzone.files);
            }
        }
    });

    // Selezione di una cartella: l'input con webkitdirectory restituisce tutti i file con il loro percorso relativo (webkitRelativePath)
    const input = $('<input type="file" webkitdirectory multiple hidden />').appendTo($(dropzone.element).closest(".incore-fupload"));

    $("#incoreFolderUploadPick").on("click", (e) => {
        // Senza stopPropagation si aprirebbe anche il selettore di file della Dropzone
        e.preventDefault();
        e.stopPropagation();
        input.trigger("click");
    });

    input.on("change", function () {
        Array.from((this as HTMLInputElement).files ?? []).forEach((file) => dropzone.addFile(file as Dropzone.DropzoneFile));
        (this as HTMLInputElement).value = "";
    });
}



/**
 * Opzioni della schermata documenti a cartelle (partial _InCoreFolderDocuments).
 */
export interface InCoreFolderDocumentsOptions {
    /** Chiave univoca dell'istanza: serve alle azioni di riga per ritrovare la schermata (es. "CommesseDocumenti"). */
    key: string;
    /** Controller documentale (es. "Commesse_Documenti"). */
    controller: string;
    /** Id dell'entità di riferimento (es. id della commessa). */
    idRiferimento: number;
    /** Id HTML del componente cartelle (partial _InCoreFolders). */
    foldersId: string;
    /** Id HTML della DataTable. */
    dtId: string;
    /** Nome della DataTable sul modello (attributo InCoreDT). */
    dtName: string;
    /** Se l'utente può gestire i documenti (carica, sposta, copia, duplica, elimina...): lo decide il progetto. */
    canManage: boolean;
    /** Se i file hanno la modale di modifica (azione Edit del controller). */
    hasEdit: boolean;
    /** Nome di una funzione globale da registrare per aggiornare la tabella (per altre pagine che devono ricaricarla). */
    refreshFunctionName?: string;
    /** Nome della proprietà della colonna (anche nascosta) con 0 per le cartelle e 1 per i file: su di essa le cartelle sono sempre prima dei file. Default "OrdineRiga". */
    folderOrderColumn?: string;
}

interface FolderDocumentsInstance {
    options: InCoreFolderDocumentsOptions;
    dt: DataTables.Api | null;
    search: string;
    /** Ricarica la DataTable (l'URL corrente comprende cartella e ricerca). */
    refresh: () => void;
    /** Ricarica la DataTable e il breadcrumb: dopo operazioni sulle cartelle la cartella corrente potrebbe essere cambiata. */
    refreshWithFolders: () => void;
}

const documentInstances = new Map<string, FolderDocumentsInstance>();

/** Millisecondi senza digitare prima che la ricerca vada al server. */
const SEARCH_DELAY = 400;

function buildDocumentsUrl(options: InCoreFolderDocumentsOptions, folderId: number | "" | null, search: string): string {
    const params: string[] = [];

    if (folderId) {
        params.push(`folderId=${folderId}`);
    }

    if (search) {
        params.push(`q=${encodeURIComponent(search)}`);
    }

    return `/${options.controller}/GetFolderDT/${options.idRiferimento}${params.length > 0 ? "?" + params.join("&") : ""}`;
}

function buildActions(options: InCoreFolderDocumentsOptions): InCoreDTActions {
    // Il codice delle azioni è una stringa eseguita al click: passa dalla funzione globale InCoreFolderDocumentsAction
    const run = (action: string, tipo: string): string => `InCoreFolderDocumentsAction('${options.key}', '${action}', '{0}', '${tipo}')`;

    const actions = new InCoreDTActions()
        .AddOnClick("Apri", "dt-doubleclick", `InCoreFoldersNavigate('${options.foldersId}', {0})`, "", "", "TipoRiga", "Cartella");

    if (!options.canManage) {
        return actions;
    }

    actions.AddHRef("Scarica zip", "", `/${options.controller}/ScaricaCartella/{0}`, "_self", "", "", "TipoRiga", "Cartella");

    if (options.hasEdit) {
        actions.AddOnClick("Gestisci", "dt-doubleclick", run("edit", "File"), "", "", "TipoRiga", "File");
    }

    actions
        .AddHRef("Download", "dt-doubleclick", `/${options.controller}/Download/{0}`, "_self", "", "", "TipoRiga", "File")
        .AddOnClick("Rinomina", "", run("rinomina", "Cartella"), "", "", "TipoRiga", "Cartella")
        .AddOnClick("Sposta in…", "", run("sposta", "Cartella"), "", "", "TipoRiga", "Cartella")
        .AddOnClick("Sposta in…", "", run("sposta", "File"), "", "", "TipoRiga", "File")
        .AddOnClick("Copia in…", "", run("copia", "Cartella"), "", "", "TipoRiga", "Cartella")
        .AddOnClick("Copia in…", "", run("copia", "File"), "", "", "TipoRiga", "File")
        .AddOnClick("Duplica", "", run("duplica", "Cartella"), "", "", "TipoRiga", "Cartella")
        .AddOnClick("Duplica", "", run("duplica", "File"), "", "", "TipoRiga", "File")
        .AddOnClick("Elimina", "", run("elimina", "Cartella"), "", "", "TipoRiga", "Cartella")
        .AddOnClick("Elimina", "", run("elimina", "File"), "", "", "TipoRiga", "File");

    return actions;
}

/**
 * Sposta la casella Cerca e il pulsante Esporta della DataTable in fondo alla riga del breadcrumb (come nel design).
 * Sono gli stessi elementi della DataTable, quindi ricerca ed export continuano a funzionare.
 */
function moveToolsToBar(options: InCoreFolderDocumentsOptions): void {
    const wrapper = $(`#${options.dtId}_wrapper`);
    const bar = $(`#${options.foldersId} .incore-folders-bar`);
    const search = wrapper.find(".dt-search").first();
    const buttons = wrapper.find(".dt-buttons");

    if (bar.length === 0 || (search.length === 0 && buttons.length === 0) || bar.find(".incore-folders-tools").length > 0) {
        return;
    }

    search.find("input").attr("placeholder", "Cerca in questa cartella");

    // La classe dt-container serve a mantenere gli stili della DataTable (scritti per gli elementi dentro .dt-container)
    const tools = $('<div class="incore-folders-tools dt-container"></div>');
    tools.append(search, buttons);
    bar.append(tools);

    // Le righe della DataTable rimaste senza contenuti non devono lasciare uno spazio vuoto
    wrapper.find(".dt-layout-row").each(function () {
        const row = $(this);
        const hasContent = row.find("button, input, select, a, table").length > 0 || row.text().trim().length > 0;
        if (!hasContent) {
            row.hide();
        }
    });
}

/**
 * Inizializza la schermata documenti a cartelle: DataTable con cartelle e file, azioni di riga,
 * ricerca nelle sottocartelle e trascinamento. Da chiamare dal partial _InCoreFolderDocuments.
 * @param {InCoreFolderDocumentsOptions} options - Opzioni della schermata.
 */
export async function InCoreFolderDocumentsInit(options: InCoreFolderDocumentsOptions): Promise<void> {
    const instance: FolderDocumentsInstance = {
        options: options,
        dt: null,
        search: "",
        refresh: () => instance.dt?.ajax.reload(),
        refreshWithFolders: () => {
            instance.refresh();
            InCoreFoldersRefresh(options.foldersId);
        }
    };
    documentInstances.set(options.key, instance);

    // Funzioni globali richieste dal pulsante di aggiornamento della DataTable e da eventuali altre pagine
    const refreshName = options.refreshFunctionName || `InCoreFolderDocumentsRefresh_${options.key}`;
    (window as any)[refreshName] = () => instance.refresh();

    const initialFolderId = InCoreFoldersFromUrl();

    const dt = await InCoreDTFromModel(
        options.dtId,
        options.dtName,
        buildDocumentsUrl(options, initialFolderId, ""),
        buildActions(options),
        `/${options.controller}/`,
        undefined,
        refreshName
    );

    if (!dt) {
        return;
    }

    instance.dt = dt;

    // Cartelle sempre prima dei file, anche ordinando per un altra colonna: ordinamento fisso sulla colonna nascosta (0 per le cartelle, 1 per i file)
    const folderOrderIndex = (dt.columns().dataSrc().toArray() as string[]).indexOf(options.folderOrderColumn || "OrdineRiga");
    if (folderOrderIndex >= 0) {
        dt.order.fixed({ pre: [[folderOrderIndex, "asc"]] }).draw();
    } else {
        console.error(`InCoreFolderDocuments: nella DataTable "${options.dtName}" manca la colonna "${options.folderOrderColumn || "OrdineRiga"}" (IInCoreFolderDocument.OrdineRiga con [InCoreDTColumn]): le cartelle non saranno ordinate prima dei file.`);
    }

    // Le azioni di riga dipendono da TipoRiga: non è una colonna ma deve essere nei dati delle righe
    dt.one("xhr.dt", (_e, _settings, json: any) => {
        const rows = Array.isArray(json) ? json : json?.data;
        if (Array.isArray(rows) && rows.length > 0 && !("TipoRiga" in rows[0])) {
            console.error(`InCoreFolderDocuments: le righe della DataTable "${options.dtName}" non hanno la proprietà "TipoRiga" (IInCoreFolderDocument.TipoRiga): le azioni di riga non funzioneranno.`);
        }
    });

    // Cerca ed Esporta della DataTable vanno in fondo alla riga del breadcrumb. I pulsanti di export sono pronti solo a inizializzazione finita
    if ((dt.settings()[0] as any)._bInitComplete) {
        moveToolsToBar(options);
    } else {
        dt.one("init.dt", () => moveToolsToBar(options));
    }

    // Cambio cartella: la ricerca riparte da zero e la DataTable ricarica con la cartella corrente
    InCoreFoldersInit(options.foldersId, initialFolderId, (folderId) => {
        instance.search = "";
        dt.search("");
        $(`#${options.dtId}_wrapper .dt-search input`).val("");
        dt.ajax.url(buildDocumentsUrl(options, folderId, "")).load();
    });

    // Ricerca anche nelle sottocartelle: il testo va al server, che cerca dalla cartella corrente in giù.
    // Si ascolta l'evento "search" dell'API DataTables (scatta quando si scrive nella casella Cerca), senza dipendere dall'HTML della casella
    let searchTimer: number | undefined;
    dt.on("search.dt", () => {
        const text = (dt.search() || "").trim();
        if (text === instance.search) {
            return;
        }

        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
            if (text === instance.search) {
                return;
            }

            instance.search = text;
            dt.ajax.url(buildDocumentsUrl(options, InCoreFoldersGetCurrent(options.foldersId), text)).load();
        }, SEARCH_DELAY);
    });

    // Trascinamento di file e cartelle su una cartella o sul breadcrumb (solo desktop, stessi permessi di "Sposta in…")
    InCoreFoldersEnableDragDrop(options.foldersId, dt, options.canManage, () => instance.refresh());
}

/**
 * Apre la modale di caricamento (file o cartella) nella cartella corrente.
 * @param {string} key - Chiave della schermata.
 * @param {string} tipo - Tipo di caricamento: "Cartella" apre Carica cartella, ogni altro tipo Carica file.
 */
export function InCoreFolderDocumentsUpload(key: string, tipo: string): void {
    const instance = documentInstances.get(key);
    if (!instance) {
        return;
    }

    const options = instance.options;
    const isFolder = tipo === "Cartella";
    InCoreModal(
        options.controller,
        InCoreModalAction.CREATE,
        isFolder ? new InCoreModalLayout().ButtonsBelowX(true, true) : new InCoreModalLayout().ButtonsBelow(true, true),
        String(options.idRiferimento),
        // Caricando una cartella possono nascere sottocartelle: si aggiorna anche il breadcrumb
        isFolder ? instance.refreshWithFolders : instance.refresh,
        isFolder ? "CaricaCartella" : "CaricaFile",
        "folderId",
        InCoreFoldersGetCurrent(options.foldersId)
    );
}

function duplicate(instance: FolderDocumentsInstance, id: string, tipo: string): void {
    const options = instance.options;
    const token = $(`#${options.foldersId} input[name="__RequestVerificationToken"]`).first().val() as string | undefined;

    $.ajax({
        url: `/${options.controller}/Duplica`,
        type: "POST",
        data: { id: id, tipo: tipo },
        headers: token ? { RequestVerificationToken: token } : {},
        success: () => {
            instance.refreshWithFolders();
            InCoreToast(InCoreToastType.Success, tipo === "Cartella" ? "Cartella duplicata con successo." : "Documento duplicato con successo.");
        },
        error: (xhr) => {
            InCoreToast(InCoreToastType.Error, xhr?.responseText || "Errore durante la duplicazione.");
        }
    });
}

/**
 * Esegue un'azione di riga della schermata documenti.
 * @param {string} key - Chiave della schermata.
 * @param {string} action - edit, rinomina, sposta, copia, duplica, elimina.
 * @param {string} id - Id della riga (cartella o file).
 * @param {string} tipo - "Cartella" oppure "File".
 */
export function InCoreFolderDocumentsAction(key: string, action: string, id: string, tipo: string): void {
    const instance = documentInstances.get(key);
    if (!instance) {
        return;
    }

    const controller = instance.options.controller;
    const isFolder = tipo === "Cartella";
    // Dopo un'operazione su una cartella si aggiorna anche il breadcrumb
    const refresh = isFolder ? instance.refreshWithFolders : instance.refresh;
    const formLayout = () => new InCoreModalLayout().ButtonsBelowX(true, true);

    switch (action) {
        case "edit":
            InCoreModal(controller, InCoreModalAction.EDIT, formLayout(), id, instance.refresh, "Edit");
            break;
        case "rinomina":
            InCoreModal(controller, InCoreModalAction.EDIT, formLayout(), id, instance.refreshWithFolders, "RinominaCartella");
            break;
        case "sposta":
            InCoreModal(controller, InCoreModalAction.EDIT, formLayout(), id, refresh, "SpostaIn", "tipo", tipo);
            break;
        case "copia":
            InCoreModal(controller, InCoreModalAction.EDIT, formLayout(), id, refresh, "CopiaIn", "tipo", tipo);
            break;
        case "duplica":
            duplicate(instance, id, tipo);
            break;
        case "elimina":
            if (isFolder) {
                InCoreModal(controller, InCoreModalAction.DELETE, new InCoreModalLayout().ButtonsBelow(true, true), id, instance.refreshWithFolders, "EliminaCartella");
            } else {
                InCoreModal(controller, InCoreModalAction.DELETE, new InCoreModalLayout().ButtonsBelow(true, true), id, () => {
                    instance.refresh();
                    InCoreToast(InCoreToastType.Success, "Documento eliminato con successo.");
                });
            }
            break;
    }
}
